import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone, Lock, User as UserIcon, KeyRound, MapPin, Gift } from "lucide-react";

// Industry-standard password rule: at least 8 chars, one letter + one number.
const PASSWORD_RE = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;

export default function Auth() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState("login"); // login | register | otp
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "both", location: "", default_catalog_privacy: "public", referral_code: "" });
  const [otpCode, setOtpCode] = useState("");
  const [pendingEmail, setPendingEmail] = useState("");
  const [demoOtp, setDemoOtp] = useState("");

  // Auto-fill referral code from URL ?ref=xxx and auto-open register tab
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setForm((f) => ({ ...f, referral_code: ref.toLowerCase() }));
      setMode("register");
    }
  }, [searchParams]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.password) {
      toast.error(t("auth.fill_all"));
      return;
    }
    if (!PASSWORD_RE.test(form.password)) {
      toast.error(t("auth.password_invalid"));
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/auth/register", form);
      setPendingEmail(r.data.email);
      setDemoOtp(r.data.demo_otp);
      setMode("otp");
      toast.success(t("auth.otp_sent"));
    } catch (err) {
      toast.error(formatApiError(err) || t("auth.register_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email: form.email, password: form.password });
      if (r.data.requires_otp) {
        setPendingEmail(r.data.email);
        setDemoOtp(r.data.demo_otp);
        setMode("otp");
        toast.info(t("auth.otp_sent"));
      } else {
        login(r.data.token, r.data.user);
        toast.success(`${t("auth.welcome_user")} ${r.data.user.name}!`);
        navigate("/dashboard");
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "";
      if (status === 429) {
        toast.error(t("auth.login_failed"));
      } else if (status === 403 && typeof detail === "string" && detail.toLowerCase().includes("admin")) {
        toast.info(t("auth.login_admin"));
        setTimeout(() => navigate("/admin/login"), 1200);
      } else {
        toast.error(formatApiError(err) || t("auth.login_failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/auth/verify-otp", { email: pendingEmail, code: otpCode });
      login(r.data.token, r.data.user);
      toast.success(t("auth.account_verified"));
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err) || t("auth.otp_wrong"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid grid-cols-1 lg:grid-cols-2 gap-0 border border-zinc-200 bg-white">
      <div className="hidden lg:block relative overflow-hidden bg-[#002FA7] p-12">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
        <div className="relative z-10 h-full flex flex-col justify-between text-white">
          <div className="label-caps opacity-80">Jastip-Safe</div>
          <div>
            <div className="font-display text-5xl leading-[0.95] tracking-tighter mb-6">
              {t("auth.welcome_back")}.
            </div>
            <p className="text-white/80 max-w-sm leading-relaxed">
              {t("hero.desc")}
            </p>
          </div>
          <div className="font-mono text-xs opacity-70">v1.0 · DEMO MODE</div>
        </div>
      </div>

      <div className="p-8 sm:p-12 flex flex-col justify-center">
        {mode === "otp" ? (
          <form onSubmit={handleVerifyOtp} className="space-y-6 max-w-md" data-testid="otp-form">
            <div>
              <div className="label-caps text-zinc-500 mb-2">OTP</div>
              <h2 className="font-display text-3xl tracking-tight">{t("auth.otp_title")}</h2>
              <p className="text-sm text-zinc-600 mt-2">
                {t("auth.otp_sent_to")} <span className="font-mono">{pendingEmail}</span>
              </p>
              {demoOtp && (
                <div className="mt-3 p-3 border border-amber-200 bg-amber-50 text-xs font-mono">
                  {t("auth.demo_mode_note")} <strong>{demoOtp}</strong>
                </div>
              )}
            </div>

            <div>
              <label className="label-caps text-zinc-500 mb-2 block">{t("auth.otp_title")}</label>
              <div className="relative">
                <KeyRound size={16} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  className="input-flat pl-12 font-mono text-2xl tracking-[0.5em] text-center"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="000000"
                  data-testid="otp-input"
                />
              </div>
            </div>

            <button type="submit" className="btn-primary-flat w-full" disabled={loading} data-testid="verify-otp-btn">
              {loading ? t("auth.verifying") : t("auth.verify_continue")}
              <ArrowRight size={16} strokeWidth={1.5} />
            </button>

            <button
              type="button"
              onClick={() => setMode("login")}
              className="text-sm text-zinc-500 hover:text-[#002FA7] transition-colors"
              data-testid="back-to-login-btn"
            >
              ← {t("auth.back_login")}
            </button>
          </form>
        ) : mode === "register" ? (
          <form onSubmit={handleRegister} className="space-y-5 max-w-md" data-testid="register-form">
            <div>
              <div className="label-caps text-zinc-500 mb-2">{t("auth.create_account")}</div>
              <h2 className="font-display text-3xl tracking-tight">{t("auth.join")}</h2>
              <p className="text-sm text-zinc-600 mt-2" data-testid="unified-account-note">
                {t("auth.unified_note")}
              </p>
            </div>

            <FormField icon={UserIcon} label={t("auth.full_name")} testid="register-name">
              <input className="input-flat pl-11" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="John Doe" data-testid="register-name-input" />
            </FormField>
            <FormField icon={Mail} label={t("auth.email")} testid="register-email">
              <input type="email" className="input-flat pl-11" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" data-testid="register-email-input" />
            </FormField>
            <FormField icon={Phone} label={t("auth.phone")} testid="register-phone">
              <input className="input-flat pl-11" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+62 812 3456 7890" data-testid="register-phone-input" />
            </FormField>
            <FormField icon={MapPin} label={t("auth.location")} testid="register-location">
              <input className="input-flat pl-11" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="e.g. Jakarta, Bali, Medan" data-testid="register-location-input" />
            </FormField>
            <FormField icon={Gift} label={t("auth.referral_label")} testid="register-referral">
              <input className="input-flat pl-11 font-mono lowercase" value={form.referral_code} onChange={(e) => set("referral_code", e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))} placeholder={t("auth.referral_placeholder")} maxLength={32} data-testid="register-referral-input" />
            </FormField>
            <FormField icon={Lock} label={t("auth.password")} testid="register-password">
              <input type="password" className="input-flat pl-11" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="••••••••" data-testid="register-password-input" />
            </FormField>
            <div className="text-xs text-zinc-500 -mt-3" data-testid="password-hint">
              {t("auth.password_hint")}
              {form.password && (
                <span className={`ml-2 font-medium ${PASSWORD_RE.test(form.password) ? "text-green-600" : "text-amber-600"}`}>
                  {PASSWORD_RE.test(form.password) ? `✓ ${t("auth.password_strong")}` : t("auth.password_weak")}
                </span>
              )}
            </div>

            {/* Privacy default — every user is also a jastiper under unified accounts */}
            <div className="border-2 p-3 space-y-2" style={{borderColor: "var(--app-border)"}} data-testid="register-privacy-section">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Lock size={14} strokeWidth={1.5} /> {t("auth.privacy_label")}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => set("default_catalog_privacy", "public")}
                    className="border p-2 text-xs text-left"
                    style={{
                      borderColor: form.default_catalog_privacy === "public" ? "var(--app-brand)" : "var(--app-border)",
                      backgroundColor: form.default_catalog_privacy === "public" ? "var(--app-brand-soft)" : "transparent",
                    }}
                    data-testid="register-privacy-public"
                  >
                    <div className="font-medium">{t("auth.privacy_public")}</div>
                    <div className="text-[10px]" style={{color: "var(--app-muted)"}}>{t("auth.privacy_public_desc")}</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => set("default_catalog_privacy", "hide_price")}
                    className="border p-2 text-xs text-left"
                    style={{
                      borderColor: form.default_catalog_privacy === "hide_price" ? "var(--app-brand)" : "var(--app-border)",
                      backgroundColor: form.default_catalog_privacy === "hide_price" ? "var(--app-brand-soft)" : "transparent",
                    }}
                    data-testid="register-privacy-hide-price"
                  >
                    <div className="font-medium">{t("auth.privacy_hide")}</div>
                    <div className="text-[10px]" style={{color: "var(--app-muted)"}}>{t("auth.privacy_hide_desc")}</div>
                  </button>
                </div>
                <p className="text-[10px]" style={{color: "var(--app-muted)"}}>
                  {t("auth.privacy_hint")}
                </p>
              </div>

            <button type="submit" className="btn-primary-flat w-full" disabled={loading} data-testid="register-submit-btn">
              {loading ? t("auth.creating") : t("auth.create_account")}
              <ArrowRight size={16} strokeWidth={1.5} />
            </button>

            <p className="text-sm text-zinc-600">
              {t("auth.already_account")}{" "}
              <button type="button" onClick={() => setMode("login")} className="text-[#002FA7] font-medium" data-testid="switch-to-login-btn">
                {t("auth.sign_in")}
              </button>
            </p>
          </form>
        ) : (
          <form onSubmit={handleLogin} className="space-y-5 max-w-md" data-testid="login-form">
            <div>
              <div className="label-caps text-zinc-500 mb-2">{t("auth.welcome_back")}</div>
              <h2 className="font-display text-3xl tracking-tight">{t("auth.sign_in")}</h2>
            </div>

            <FormField icon={Mail} label={t("auth.email")} testid="login-email">
              <input type="email" className="input-flat pl-11" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" data-testid="login-email-input" />
            </FormField>
            <FormField icon={Lock} label={t("auth.password")} testid="login-password">
              <input type="password" className="input-flat pl-11" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="••••••••" data-testid="login-password-input" />
            </FormField>

            <button type="submit" className="btn-primary-flat w-full" disabled={loading} data-testid="login-submit-btn">
              {loading ? t("auth.signing_in") : t("nav.sign_in")}
              <ArrowRight size={16} strokeWidth={1.5} />
            </button>

            <div className="border-t pt-5 mt-2" style={{borderColor: "var(--app-border)"}}>
              <p className="text-base sm:text-lg text-center" style={{color: "var(--app-muted)"}}>
                {t("auth.no_account")}
              </p>
              <button
                type="button"
                onClick={() => setMode("register")}
                className="block w-full text-center text-xl sm:text-2xl font-display font-semibold tracking-tight mt-1 hover:underline"
                style={{color: "var(--app-brand)"}}
                data-testid="switch-to-register-btn"
              >
                {t("auth.create_one")} →
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function FormField({ icon: Icon, label, testid, children }) {
  return (
    <div>
      <label className="label-caps text-zinc-500 mb-2 block" data-testid={`${testid}-label`}>{label}</label>
      <div className="relative">
        <Icon size={16} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
        {children}
      </div>
    </div>
  );
}
